import { db } from "../../firebase/config";
import { collection, query, where, getDocs } from "firebase/firestore";

/**
 * Récupère le nombre total d'administrateurs (admin + PDG)
 * @returns {Promise<number>} Nombre total d'administrateurs
 */
export const getAdminCount = async () => {
  try {
    const adminQuery = query(
      collection(db, "users"),
      where("role", "in", ["admin", "PDG"])
    );
    
    const adminSnapshot = await getDocs(adminQuery);
    return adminSnapshot.size;
  } catch (error) {
    console.error("Erreur lors du comptage des administrateurs:", error);
    throw error;
  }
};

/**
 * Récupère le nombre d'agriculteurs en attente d'approbation
 * @returns {Promise<number>} Nombre d'agriculteurs en attente
 */
export const getPendingFarmersCount = async () => {
  try {
    // Utilisation du champ 'status' pour être cohérent avec getPendingFarmers()
    const pendingQuery = query(
      collection(db, "farmers"),
      where("status", "==", "pending")
    );
    
    const pendingSnapshot = await getDocs(pendingQuery);
    return pendingSnapshot.size;
  } catch (error) {
    console.error("Erreur lors du comptage des agriculteurs en attente:", error);
    throw error;
  }
};

/**
 * Récupère le nombre de demandes d'administrateurs en attente
 * @returns {Promise<number>} Nombre de demandes d'administrateurs en attente
 */
export const getPendingAdminRequestsCount = async () => {
  try {
    const pendingQuery = query(
      collection(db, "admin_requests"),
      where("status", "==", "pending")
    );
    
    const pendingSnapshot = await getDocs(pendingQuery);
    return pendingSnapshot.size;
  } catch (error) {
    console.error("Erreur lors du comptage des demandes d'administrateurs en attente:", error);
    return 0; // Retourner 0 en cas d'erreur pour ne pas bloquer l'application
  }
};

/**
 * Récupère le nombre total de tous les utilisateurs en attente (agriculteurs et administrateurs)
 * @returns {Promise<number>} Nombre total d'utilisateurs en attente
 */
export const getTotalPendingCount = async () => {
  try {
    const pendingFarmersCount = await getPendingFarmersCount();
    const pendingAdminCount = await getPendingAdminRequestsCount();
    
    return pendingFarmersCount + pendingAdminCount;
  } catch (error) {
    console.error("Erreur lors du comptage total des utilisateurs en attente:", error);
    throw error;
  }
};

/**
 * Récupère le nombre d'agriculteurs approuvés
 * @returns {Promise<number>} Nombre d'agriculteurs approuvés
 */
export const getApprovedFarmersCount = async () => {
  try {
    // Utilisation du champ 'status' au lieu de 'adminApproved'
    // pour assurer la cohérence avec getApprovedFarmers()
    const approvedQuery = query(
      collection(db, "farmers"),
      where("status", "==", "approved")
    );
    
    const approvedSnapshot = await getDocs(approvedQuery);
    return approvedSnapshot.size;
  } catch (error) {
    console.error("Erreur lors du comptage des agriculteurs approuvés:", error);
    throw error;
  }
};

/**
 * Récupère la liste des agriculteurs approuvés
 * @returns {Promise<Array>} Liste des agriculteurs approuvés
 */
export const getApprovedFarmers = async () => {
  try {
    // Utilisation du champ 'status' au lieu de combiner adminApproved avec orderBy
    // pour éviter l'erreur d'index manquant
    const approvedQuery = query(
      collection(db, "farmers"),
      where("status", "==", "approved")
    );
    
    const approvedSnapshot = await getDocs(approvedQuery);
    // Trier les résultats côté client après récupération
    const farmers = approvedSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Tri par date de création décroissante (du plus récent au plus ancien)
    return farmers.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error("Erreur lors de la récupération des agriculteurs approuvés:", error);
    throw error;
  }
};

/**
 * Récupère la liste des agriculteurs en attente d'approbation
 * @returns {Promise<Array>} Liste des agriculteurs en attente
 */
export const getPendingFarmers = async () => {
  try {
    const pendingQuery = query(
      collection(db, "farmers"),
      where("status", "==", "pending")
    );
    
    const pendingSnapshot = await getDocs(pendingQuery);
    // Trier les résultats côté client après récupération
    const farmers = pendingSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Tri par date de création décroissante (du plus récent au plus ancien)
    return farmers.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error("Erreur lors de la récupération des agriculteurs en attente:", error);
    throw error;
  }
};

/**
 * Récupère la liste des demandes d'administrateurs en attente d'approbation
 * @returns {Promise<Array>} Liste des demandes d'administrateurs en attente
 */
export const getPendingAdminRequests = async () => {
  try {
    const pendingQuery = query(
      collection(db, "admin_requests"),
      where("status", "==", "pending")
    );
    
    const pendingSnapshot = await getDocs(pendingQuery);
    // Trier les résultats côté client après récupération
    const adminRequests = pendingSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    // Tri par date de création décroissante (du plus récent au plus ancien)
    return adminRequests.sort((a, b) => {
      const dateA = a.createdAt?.toDate?.() || new Date(0);
      const dateB = b.createdAt?.toDate?.() || new Date(0);
      return dateB - dateA;
    });
  } catch (error) {
    console.error("Erreur lors de la récupération des demandes d'administrateurs en attente:", error);
    throw error;
  }
};

export const getNewDiagnosticRequestsCount = async () => {
  try {
    const q = query(collection(db, "diagnostic_requests"), where("status", "==", "new"));
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error("Erreur lors du comptage des diagnostics:", error);
    return 0;
  }
};
